import React, { createContext, forwardRef, useEffect, useState } from 'react'

// This component use Compound pattern
// Refernce: https://kentcdodds.com/blog/compound-components-with-react-hooks

export type RadioGroupContext = {
  onChange: (value: string | boolean) => void
  selectedValue: string | boolean | null
}

const RadioGroupContext = createContext<RadioGroupContext | null>(null)

export type RadioGroupProps = {
  selectedValue?: string | boolean
  onChange?: (value: string | boolean) => void
}

export const RadioGroup = forwardRef<
  () => HTMLDivElement,
  WithChildren<RadioGroupProps>
>(({ selectedValue, onChange, children, ...rest }, ref) => {
  const [internalSelectedValue, setInternalSelectedValue] = useState<
    string | boolean | null
  >(selectedValue || null)

  useEffect(() => {
    if (selectedValue === undefined) return

    setInternalSelectedValue(selectedValue)
  }, [selectedValue])

  const onValueChanged = React.useCallback(
    (value: string | boolean) => {
      setInternalSelectedValue(value)

      if (onChange) onChange(value)
    },
    [onChange]
  )

  const value = React.useMemo(
    () => ({ onChange: onValueChanged, selectedValue: internalSelectedValue }),
    [onValueChanged, internalSelectedValue]
  )

  return (
    <RadioGroupContext.Provider value={value}>
      <div
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={ref as any} // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/28884
        {...rest}
        // UNIT TEST
        data-testid='radio-group-container'
      >
        {children}
      </div>
    </RadioGroupContext.Provider>
  )
})

RadioGroup.displayName = 'RadioGroup' // Typescript workaround for ForwardRef Component

export function useRadioGroupContext(): RadioGroupContext {
  const context = React.useContext(RadioGroupContext)
  if (!context) {
    throw new Error(`useRadioGroupContext must be used within a Radio Group`)
  }
  return context
}
